import { JSONEditor } from '@json-editor/json-editor'
import _ from 'lodash'
import Choices from 'choices.js'

const StringEditor = JSONEditor.defaults.editors.string

class CSVArray extends StringEditor {
  build () {
    const directionsLabel = this.options.directionsLabel || 'Type and press enter to add items. Drag to reorder, click X to remove.'
    if (!this.options.compact) this.header = this.label = this.theme.getFormInputLabel(directionsLabel, this.isRequired())
    if (this.schema.description) this.description = this.theme.getFormInputDescription(this.translateProperty(this.schema.description))
    if (this.options.infoText) this.infoButton = this.theme.getInfoButton(this.translateProperty(this.options.infoText))

    this.input = this.theme.getFormInputField('text')
    this.usingTagsFormat = this.schema.format === 'tags'
    if (this.options.input_width) this.input.style.width = this.options.input_width

    this.input.addEventListener('change', e => {
      e.preventDefault()
      e.stopPropagation()
      const sanitized = this.sanitize(e.currentTarget.value)
      if (e.currentTarget.value !== sanitized) e.currentTarget.value = sanitized
      this.is_dirty = true
      this.refreshValue()
    })

    if (this.usingTagsFormat) {
      this.input.addEventListener('blur', () => this.choices.clearInput())
    }

    this.header = document.createElement('span')
    this.header.textContent = this.getTitle()
    this.title = this.theme.getHeader(this.header)
    this.container.appendChild(this.title)

    this.control = this.theme.getFormControl(this.label, this.input, this.description, this.infoButton, this.formname)
    this.container.appendChild(this.control)

    if (this.usingTagsFormat) {
      this.choices = new Choices(this.input, {
        addItems: true,
        duplicateItemsAllowed: false,
        delimiter: ',',
        editItems: false,
        removeItemButton: true
      })

      this.choices.input.element.addEventListener('blur', () => this.choices.clearInput())
      setTimeout(() => {
        if (!this.dragInitialized) {
          this.dragInitialized = true
          this.initDragAndDrop()
        }
      }, 100)
      this.choices._handleBackspace = function(items) {
        if (!this.input.value) {
          return;
        }
      };
    }

    this.refreshValue()
  }

  initDragAndDrop() {
    const itemList = this.choices.containerOuter.element.querySelector('.choices__list--multiple')
    if (!itemList) return

    let draggedElement = null
    let isDragging = false
    this.dragListeners = new Map() // Track listeners for cleanup

    // Filter to get only real tag elements (excludes Choices.js internal duplicates)
    const getValidItems = (container) => [...container.querySelectorAll('.choices__item')]
      .filter(el => el.dataset.value && !el.classList.contains('is-highlighted'))

    const getDragAfterElement = (container, dragged, x, y) => {
      const elements = getValidItems(container).filter(el => el !== dragged)
      
      // Find elements in the same row as cursor (Y coordinate matches)
      const targetRow = elements.filter(el => {
        const box = el.getBoundingClientRect()
        return y >= box.top && y <= box.bottom
      })

      // If cursor is on a row, find insertion point based on X position
      if (targetRow.length > 0) {
        for (const child of targetRow) {
          const box = child.getBoundingClientRect()
          // Insert before this element if cursor is left of its center
          if (x < box.left + box.width / 2) return child
        }
        // Cursor is after all elements in row, so insert at end of row
        return targetRow[targetRow.length - 1].nextElementSibling
      }

      // Cursor is between rows - find closest element using weighted distance
      // (Y distance is weighted 2x more to prioritize row selection)
      let closest = null
      let minDist = Infinity
      for (const el of elements) {
        const box = el.getBoundingClientRect()
        const dist = Math.abs(y - (box.top + box.height / 2)) * 2 + Math.abs(x - (box.left + box.width / 2))
        if (dist < minDist) {
          minDist = dist
          closest = el
        }
      }

      if (closest) {
        const box = closest.getBoundingClientRect()
        // Insert before or after the closest element based on X position
        return x < box.left + box.width / 2 ? closest : closest.nextElementSibling
      }
      return null
    }

    const makeDraggable = () => {
      itemList.querySelectorAll('.choices__item').forEach(item => {
        if (!item.dataset.dragListener && item.dataset.value) {
          item.addEventListener('mousedown', onMouseDown)
          item.dataset.dragListener = 'true'
          this.dragListeners.set(item, onMouseDown) // Track for cleanup
        }
      })
    }

    const onMouseDown = (e) => {
      if (e.target.classList.contains('choices__button')) return // Don't drag when clicking X button
      const item = e.target.closest('.choices__item')
      if (!item?.dataset.value) return

      // Block Choices.js from refreshing while we're dragging
      this.isManuallyReordering = true
      draggedElement = item
      isDragging = false
      
      const startX = e.clientX, startY = e.clientY

      const onMouseMove = (e) => {
        if (!draggedElement) return
        // Calculate distance from initial click - prevents accidental drags
        const distance = Math.hypot(e.clientX - startX, e.clientY - startY)
        
        // Start drag only after moving 5px (threshold)
        if (!isDragging && distance > 5) {
          isDragging = true
          observer.disconnect() // Pause observer during drag to avoid conflicts
          draggedElement.classList.add('dragging')
        }

        if (!isDragging) return

        // Find where to insert based on current mouse position
        const afterElement = getDragAfterElement(itemList, draggedElement, e.clientX, e.clientY)
        if (afterElement === null) {
          // Insert at end
          const lastChild = getValidItems(itemList).pop()
          if (draggedElement !== lastChild) itemList.appendChild(draggedElement)
        } else if (afterElement !== draggedElement && afterElement.previousElementSibling !== draggedElement) {
          // Insert before the target element
          itemList.insertBefore(draggedElement, afterElement)
        }
      }

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)

        // Restore visual state
        if (draggedElement) {
          draggedElement.classList.remove('dragging')
        }

        if (isDragging && draggedElement) {
          // Get final order from DOM and remove any duplicates
          const items = getValidItems(itemList)
          const seen = new Set()
          const uniqueItems = items.filter(item => {
            if (seen.has(item.dataset.value)) {
              item.remove() // Remove duplicate DOM elements
              return false
            }
            seen.add(item.dataset.value)
            return true
          })
          
          // Update internal state with new order
          const newOrder = uniqueItems.map(item => item.dataset.value)
          this.value = newOrder.join(',')
          this.input.value = this.value
          this.is_dirty = true
          
          // Sync Choices.js with new order (this recreates DOM)
          this.choices.clearStore()
          this.choices.setValue(newOrder)
          setTimeout(() => makeDraggable(), 10) // Re-apply drag listeners to new DOM
          
          // Re-enable observer and trigger change event
          setTimeout(() => {
            observer.observe(itemList, { childList: true, subtree: false })
            this.isManuallyReordering = false
            this.onChange(true)
          }, 100)
        } else {
          // User didn't actually drag, just clicked - clean up flag
          setTimeout(() => { this.isManuallyReordering = false }, 100)
          if (!isDragging) observer.observe(itemList, { childList: true, subtree: false })
        }

        draggedElement = null
        isDragging = false
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }

    makeDraggable()

    // Watch for new tags added by user - automatically make them draggable
    const observer = new MutationObserver((mutations) => {
      if (mutations.some(m => m.type === 'childList' && m.addedNodes.length > 0)) {
        setTimeout(() => makeDraggable(), 10)
      }
    })
    observer.observe(itemList, { childList: true, subtree: false })

    // Cleanup function to prevent memory leaks
    this.dragCleanup = () => {
      observer.disconnect()
      // Remove all event listeners
      this.dragListeners.forEach((listener, item) => {
        item.removeEventListener('mousedown', listener)
      })
      this.dragListeners.clear()
    }
  }

  getCleanedArrayFromString (str) {
    if (typeof str !== 'string') throw new Error('Value must be a string')
    return _.uniq(str.split(',').map(x => x.trim()).filter(x => x))
  }

  setValue (value, initial) {
    if (value === null) value = ''
    else if (Array.isArray(value)) value = value.join(',')
    if (typeof value !== 'string') throw new Error('Value must be a string')

    this.value = this.getCleanedArrayFromString(value).join(',')
    this.input.value = this.value
    this.refreshValue()
    if (initial) this.is_dirty = false
  }

  refreshValue () {
    // Don't refresh if we're manually reordering via drag-and-drop
    if (this.isManuallyReordering) return

    this.value = this.input.value
    if (this.usingTagsFormat) {
      this.choices.clearStore()
      this.choices.setValue(this.getCleanedArrayFromString(this.value))
    }
    this.onChange(true)
  }

  getValue () {
    if (!this.input?.value) return []
    return this.getCleanedArrayFromString(this.value)
  }

  destroy () {
    // Clean up drag-and-drop listeners when editor is removed
    if (this.dragCleanup) this.dragCleanup()
    super.destroy()
  }
}

export default CSVArray
